if (!customElements.get("velvet-announcement-slider")) {
  class VelvetAnnouncementSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(
        this.querySelectorAll("[data-velvet-announcement-slide]"),
      );
      this.previousButton = this.querySelector(
        "[data-velvet-announcement-previous]",
      );
      this.nextButton = this.querySelector("[data-velvet-announcement-next]");
      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.hasAttribute("data-active")),
      );
      this.autoplayTimer = null;
      this.touchStartX = null;

      this.sliderEnabled =
        this.dataset.sliderEnabled === "true" && this.slides.length > 1;
      this.autoplayEnabled =
        this.sliderEnabled && this.dataset.autoplay === "true";
      this.autoplaySpeed =
        Math.max(3, Number.parseInt(this.dataset.autoplaySpeed, 10) || 8) *
        1000;
      this.pauseOnHover = this.dataset.pauseOnHover === "true";
      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      this.handlePreviousClick = () => this.move(-1, true);
      this.handleNextClick = () => this.move(1, true);
      this.handleMouseEnter = () => this.stopAutoplay();
      this.handleMouseLeave = () => this.startAutoplay();
      this.handleFocusIn = () => this.stopAutoplay();
      this.handleFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      };
      this.handleVisibilityChange = () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      };
      this.handleTouchStart = (event) => {
        this.touchStartX = event.changedTouches[0].clientX;
        this.stopAutoplay();
      };
      this.handleTouchEnd = (event) => {
        if (this.touchStartX === null) return;
        const distance = event.changedTouches[0].clientX - this.touchStartX;

        if (Math.abs(distance) >= 45) {
          this.move(distance > 0 ? -1 : 1, true);
        } else {
          this.startAutoplay();
        }

        this.touchStartX = null;
      };
      this.handleBlockSelect = (event) => {
        if (!this.contains(event.target)) return;

        const selectedSlide = event.target.closest(
          "[data-velvet-announcement-slide]",
        );
        if (!selectedSlide) return;

        const selectedIndex = this.slides.indexOf(selectedSlide);
        if (selectedIndex >= 0) {
          this.stopAutoplay();
          this.showSlide(selectedIndex);
        }
      };

      this.showSlide(this.currentIndex);

      if (!this.sliderEnabled) return;

      this.previousButton?.addEventListener(
        "click",
        this.handlePreviousClick,
      );
      this.nextButton?.addEventListener("click", this.handleNextClick);
      this.addEventListener("focusin", this.handleFocusIn);
      this.addEventListener("focusout", this.handleFocusOut);
      this.addEventListener("touchstart", this.handleTouchStart, {
        passive: true,
      });
      this.addEventListener("touchend", this.handleTouchEnd, {
        passive: true,
      });
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange,
      );
      document.addEventListener("shopify:block:select", this.handleBlockSelect);

      if (this.pauseOnHover) {
        this.addEventListener("mouseenter", this.handleMouseEnter);
        this.addEventListener("mouseleave", this.handleMouseLeave);
      }

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.previousButton?.removeEventListener(
        "click",
        this.handlePreviousClick,
      );
      this.nextButton?.removeEventListener("click", this.handleNextClick);
      this.removeEventListener("focusin", this.handleFocusIn);
      this.removeEventListener("focusout", this.handleFocusOut);
      this.removeEventListener("touchstart", this.handleTouchStart);
      this.removeEventListener("touchend", this.handleTouchEnd);
      this.removeEventListener("mouseenter", this.handleMouseEnter);
      this.removeEventListener("mouseleave", this.handleMouseLeave);
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange,
      );
      document.removeEventListener(
        "shopify:block:select",
        this.handleBlockSelect,
      );
    }

    move(direction, userInitiated = false) {
      const nextIndex =
        (this.currentIndex + direction + this.slides.length) %
        this.slides.length;

      this.showSlide(nextIndex);

      if (userInitiated) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    showSlide(index) {
      this.currentIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.currentIndex;
        slide.toggleAttribute("data-active", isActive);
        slide.setAttribute("aria-hidden", String(!isActive));

        const link = slide.querySelector("a");
        if (link) link.tabIndex = isActive ? 0 : -1;
      });
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.reducedMotion ||
        document.hidden ||
        this.autoplayTimer
      ) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.move(1);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define(
    "velvet-announcement-slider",
    VelvetAnnouncementSlider,
  );
}
